import fetchWithTenant from '../utils/fetchWithTenant';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map as MapIcon, Upload, Satellite, ArrowLeft, ArrowRight, Check, Loader2, Search, Ruler, Save, X, ZoomIn, ZoomOut } from 'lucide-react';
import useMapStore from '../stores/mapStore';

// ── Steps ─────────────────────────────────────────────────
const STEPS = [
  { id: 'source', label: 'Source' },
  { id: 'locate', label: 'Locate' },
  { id: 'crop', label: 'Crop & Adjust' },
  { id: 'scale', label: 'Set Scale' },
  { id: 'save', label: 'Save' },
];

const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8892b0' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2d2d44' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1538' }] },
];

export default function SurveyWizard() {
  const navigate = useNavigate();
  const { settings, fetchSettings, uploadFloorPlanImage, createSurvey } = useMapStore();

  const [step, setStep] = useState(0);
  const [sourceType, setSourceType] = useState(null); // 'satellite' or 'imported'
  const [saving, setSaving] = useState(false);

  // Satellite state
  const [address, setAddress] = useState('');
  const [satCenter, setSatCenter] = useState(null);
  const [satZoom, setSatZoom] = useState(19);
  const [mapsLoaded, setMapsLoaded] = useState(!!window.google?.maps);
  const mapRef = useRef(null);
  const mapElRef = useRef(null);
  const rectangleRef = useRef(null);

  // Crop bounds (satellite)
  const [cropBounds, setCropBounds] = useState(null);

  // Import state
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importImg, setImportImg] = useState(null);
  const [importDims, setImportDims] = useState(null);

  // Scale state
  const [scaleMode, setScaleMode] = useState(false);
  const [scalePoints, setScalePoints] = useState([]);
  const [scaleDistance, setScaleDistance] = useState('');
  const [scaleUnit, setScaleUnit] = useState('feet');
  const scaleCanvasRef = useRef(null);

  // Save form
  const [surveyName, setSurveyName] = useState('');
  const [buildingName, setBuildingName] = useState('');
  const [floorNumber, setFloorNumber] = useState(0);
  const [description, setDescription] = useState('');
  const [surveyLabel, setSurveyLabel] = useState('');
  const [surveyReference, setSurveyReference] = useState('');

  // Captured image for satellite mode
  const [capturedImage, setCapturedImage] = useState(null);

  useEffect(() => { fetchSettings(); }, []);

  // Load Google Maps script
  useEffect(() => {
    if (window.google?.maps || !settings?.googleMapsApiKey) {
      if (window.google?.maps) setMapsLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js?key=' + settings.googleMapsApiKey + '&libraries=places';
    script.async = true;
    script.onload = () => setMapsLoaded(true);
    document.head.appendChild(script);
  }, [settings?.googleMapsApiKey]);

  // ── Satellite: Address Search ───────────────────────────
  const handleAddressSearch = useCallback(() => {
    if (!window.google?.maps || !address.trim()) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: address.trim() }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        const center = { lat: loc.lat(), lng: loc.lng() };
        setSatCenter(center);
        setSatZoom(19);
        if (mapRef.current) {
          mapRef.current.setCenter(center);
          mapRef.current.setZoom(19);
        }
      }
    });
  }, [address]);

  // ── Satellite: Init map ─────────────────────────────────
  const initSatMap = useCallback((el) => {
    if (!el || mapRef.current || !window.google?.maps) return;
    mapElRef.current = el;
    const center = satCenter || { lat: 42.9957, lng: -73.8093 };
    const map = new window.google.maps.Map(el, {
      center,
      zoom: satZoom,
      mapTypeId: 'satellite',
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: false,
      gestureHandling: 'greedy',
    });
    mapRef.current = map;
  }, [satCenter, mapsLoaded]);

  // ── Satellite: Draw crop rectangle ──────────────────────
  const enableCropRect = useCallback(() => {
    if (!mapRef.current) return;
    // Remove existing
    if (rectangleRef.current) rectangleRef.current.setMap(null);

    const map = mapRef.current;
    const center = map.getCenter();
    const zoom = map.getZoom();
    // Create a rectangle roughly 60% of the visible area
    const offset = 0.0005 * Math.pow(2, 19 - zoom);
    const bounds = {
      north: center.lat() + offset,
      south: center.lat() - offset,
      east: center.lng() + offset * 1.5,
      west: center.lng() - offset * 1.5,
    };

    const rect = new window.google.maps.Rectangle({
      bounds,
      map,
      editable: true,
      draggable: true,
      strokeColor: '#00d4aa',
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: '#00d4aa',
      fillOpacity: 0.1,
    });

    rect.addListener('bounds_changed', () => {
      const b = rect.getBounds();
      setCropBounds({
        NELat: b.getNorthEast().lat(),
        NELng: b.getNorthEast().lng(),
        SWLat: b.getSouthWest().lat(),
        SWLng: b.getSouthWest().lng(),
      });
    });

    // Set initial bounds
    const b = rect.getBounds();
    setCropBounds({
      NELat: b.getNorthEast().lat(),
      NELng: b.getNorthEast().lng(),
      SWLat: b.getSouthWest().lat(),
      SWLng: b.getSouthWest().lng(),
    });

    rectangleRef.current = rect;
  }, []);

  // ── Satellite: Capture crop as image ────────────────────
  const captureSatelliteImage = useCallback(async () => {
    if (!cropBounds || !settings?.googleMapsApiKey) return;
    const zoom = Math.round(mapRef.current?.getZoom() || 19);
    const mapCenter = mapRef.current?.getCenter();
    const mapCenterLat = mapCenter?.lat() || (cropBounds.NELat + cropBounds.SWLat) / 2;
    const mapCenterLng = mapCenter?.lng() || (cropBounds.NELng + cropBounds.SWLng) / 2;
    try {
      const res = await fetchWithTenant("/api/map/capture-crop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          apiKey: settings.googleMapsApiKey,
          mapCenterLat,
          mapCenterLng,
          NELat: cropBounds.NELat,
          NELng: cropBounds.NELng,
          SWLat: cropBounds.SWLat,
          SWLng: cropBounds.SWLng,
          zoom,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setCapturedImage(data.imageUrl);
      setSatZoom(zoom);
      return data.imageUrl;
    } catch (err) {
      console.error("[SurveyWizard] captureSatelliteImage error:", err);
      return null;
    }
  }, [cropBounds, settings]);











  // ── Import: Handle file upload ──────────────────────────
  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadedFile(file);

    const uploaded = await uploadFloorPlanImage(file);
    if (uploaded) {
      setUploadedUrl(uploaded.url);
      // Load image to get dimensions
      if (file.type.startsWith('image/')) {
        const img = new Image();
        img.onload = () => {
          setImportDims({ width: img.width, height: img.height });
          setImportImg(img);
        };
        img.src = URL.createObjectURL(file);
      } else if (file.type === 'application/pdf') {
        // For PDFs, we'll use the uploaded URL
        setImportDims({ width: 800, height: 600 });
      }
    }
    setUploading(false);
  }, []);

  // ── Scale: Handle canvas clicks for scale calibration ───
  const handleScaleClick = useCallback((e) => {
    const canvas = scaleCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setScalePoints(prev => {
      if (prev.length >= 2) return [{ x, y }];
      return [...prev, { x, y }];
    });
  }, []);

  // ── Scale: Draw scale line on canvas ────────────────────
  useEffect(() => {
    const canvas = scaleCanvasRef.current;
    if (!canvas || scalePoints.length === 0) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Draw points
    scalePoints.forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x * w / 100, p.y * h / 100, 6, 0, Math.PI * 2);
      ctx.fillStyle = '#00d4aa';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    // Draw line between points
    if (scalePoints.length === 2) {
      ctx.beginPath();
      ctx.moveTo(scalePoints[0].x * w / 100, scalePoints[0].y * h / 100);
      ctx.lineTo(scalePoints[1].x * w / 100, scalePoints[1].y * h / 100);
      ctx.strokeStyle = '#00d4aa';
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [scalePoints]);

  // ── Save survey ─────────────────────────────────────────
  const handleSave = async () => {
    if (!surveyName.trim()) return;
    setSaving(true);
    // If satellite and no captured image yet, capture now
    let imageUrl = sourceType === "satellite" ? capturedImage : uploadedUrl;
    if (!imageUrl && sourceType === "satellite" && cropBounds && settings?.googleMapsApiKey) {
      const centerLat = (cropBounds.NELat + cropBounds.SWLat) / 2;
      const centerLng = (cropBounds.NELng + cropBounds.SWLng) / 2;
      const zoom = Math.round(mapRef.current?.getZoom() || 19);
    const mapCenter = mapRef.current?.getCenter();
    const mapCenterLat = mapCenter?.lat() || (cropBounds.NELat + cropBounds.SWLat) / 2;
    const mapCenterLng = mapCenter?.lng() || (cropBounds.NELng + cropBounds.SWLng) / 2;
      imageUrl = "https://maps.googleapis.com/maps/api/staticmap?visible=" + cropBounds.NELat + "," + cropBounds.NELng + "|" + cropBounds.SWLat + "," + cropBounds.SWLng + "&size=800x600&maptype=satellite&key=" + settings.googleMapsApiKey;
      setCapturedImage(imageUrl);
    }
    console.log("[SurveyWizard] Save - imageUrl:", imageUrl, "sourceType:", sourceType, "capturedImage:", capturedImage);
    if (!imageUrl) { console.error("[SurveyWizard] No imageUrl, aborting save"); setSaving(false); return; }

    const surveyData = {
      name: surveyName.trim(),
      sourceType,
      buildingName: buildingName.trim() || null,
      floorNumber: parseInt(floorNumber) || 0,
      description: description.trim() || null,
      surveyLabel: surveyLabel.trim() || null,
      surveyReference: surveyReference.trim() || null,
      imageUrl,
      mimeType: sourceType === 'satellite' ? 'image/png' : (uploadedFile?.type || 'image/png'),
      originalFilename: sourceType === 'satellite' ? 'satellite-capture.png' : (uploadedFile?.name || null),
      imageWidth: sourceType === 'satellite' ? 800 : (importDims?.width || null),
      imageHeight: sourceType === 'satellite' ? 600 : (importDims?.height || null),
    };

    // Satellite-specific fields
    if (sourceType === 'satellite' && cropBounds) {
      surveyData.address = address;
      surveyData.boundsNELat = cropBounds.NELat;
      surveyData.boundsNELng = cropBounds.NELng;
      surveyData.boundsSWLat = cropBounds.SWLat;
      surveyData.boundsSWLng = cropBounds.SWLng;
      surveyData.satelliteZoom = satZoom;
    }

    // Scale fields
    if (scalePoints.length === 2 && scaleDistance) {
      const dist = parseFloat(scaleDistance);
      surveyData.scaleDistanceFeet = scaleUnit === 'meters' ? dist * 3.28084 : dist;
      surveyData.scaleStartX = scalePoints[0].x;
      surveyData.scaleStartY = scalePoints[0].y;
      surveyData.scaleEndX = scalePoints[1].x;
      surveyData.scaleEndY = scalePoints[1].y;
    }

    const result = await createSurvey(surveyData);
    setSaving(false);
    if (result) navigate('/map');
  };

  // ── Navigation ──────────────────────────────────────────
  const canAdvance = () => {
    switch (STEPS[step].id) {
      case 'source': return !!sourceType;
      case 'locate':
        if (sourceType === 'satellite') return !!satCenter;
        return !!uploadedUrl;
      case 'crop':
        if (sourceType === 'satellite') return !!cropBounds;
        return true; // crop is optional for imported
      case 'scale': return true; // scale is optional
      case 'save': return !!surveyName.trim();
      default: return false;
    }
  };

  const goNext = async () => {
    if (step === STEPS.length - 1) {
      handleSave();
      return;
    }
    // When leaving crop step in satellite mode, capture the image
    if (STEPS[step].id === 'crop' && sourceType === 'satellite') {
      await captureSatelliteImage();
    }
    setStep(s => s + 1);
  };

  const goBack = () => setStep(s => Math.max(0, s - 1));

  // ── Common styles ───────────────────────────────────────
  const cardStyle = {
    background: 'var(--bg-secondary, #1e293b)',
    border: '1px solid var(--border-color, #334155)',
    borderRadius: '12px',
    padding: '24px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
  };

  const activeCardStyle = {
    ...cardStyle,
    borderColor: '#00d4aa',
    background: 'rgba(0,212,170,0.08)',
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    background: 'var(--bg-tertiary, #0f172a)',
    border: '1px solid var(--border-color, #334155)',
    borderRadius: '8px',
    color: 'var(--text-primary, #e2e8f0)',
    fontSize: '14px',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    color: 'var(--text-secondary, #94a3b8)',
    marginBottom: '6px',
    fontWeight: 500,
  };

  // ── Render Steps ────────────────────────────────────────

  const renderStepContent = () => {
    switch (STEPS[step].id) {

      // ── Step 1: Choose Source ─────────────────────────────
      case 'source':
        return (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{ color: 'var(--text-primary, #e2e8f0)', marginBottom: '8px', textAlign: 'center' }}>
              Choose Map Source
            </h2>
            <p style={{ color: 'var(--text-secondary, #94a3b8)', textAlign: 'center', marginBottom: '32px', fontSize: '14px' }}>
              How would you like to create this site map?
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div
                style={sourceType === 'satellite' ? activeCardStyle : cardStyle}
                onClick={() => setSourceType('satellite')}
              >
                <Satellite size={40} style={{ color: '#00d4aa', marginBottom: '12px' }} />
                <h3 style={{ color: 'var(--text-primary, #e2e8f0)', margin: '0 0 8px 0', fontSize: '16px' }}>Satellite View</h3>
                <p style={{ color: 'var(--text-secondary, #94a3b8)', margin: 0, fontSize: '13px' }}>
                  Search an address and crop from Google Maps satellite imagery. Best for exterior, rooftops, parking areas.
                </p>
              </div>
              <div
                style={sourceType === 'imported' ? activeCardStyle : cardStyle}
                onClick={() => setSourceType('imported')}
              >
                <Upload size={40} style={{ color: '#0ea5e9', marginBottom: '12px' }} />
                <h3 style={{ color: 'var(--text-primary, #e2e8f0)', margin: '0 0 8px 0', fontSize: '16px' }}>Import Floor Plan</h3>
                <p style={{ color: 'var(--text-secondary, #94a3b8)', margin: 0, fontSize: '13px' }}>
                  Upload a PDF or image of your floor plan. Best for interior layouts, hallways, individual floors.
                </p>
              </div>
            </div>
          </div>
        );

      // ── Step 2: Locate ────────────────────────────────────
      case 'locate':
        if (sourceType === 'satellite') {
          return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddressSearch()}
                  placeholder="Enter address (e.g. 100 W Lucerne Cir, Orlando, FL)"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button className="btn btn-primary" onClick={handleAddressSearch} style={{ whiteSpace: 'nowrap' }}>
                  <Search size={16} /> Search
                </button>
              </div>
              <div style={{ flex: 1, borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border-color, #334155)', minHeight: '400px' }}>
                {mapsLoaded ? (
                  <div ref={initSatMap} style={{ width: '100%', height: '100%' }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-secondary, #94a3b8)', gap: '8px' }}>
                    <Loader2 size={20} className="spin" /> Loading Google Maps...
                  </div>
                )}
              </div>
              {satCenter && (
                <p style={{ color: '#00d4aa', fontSize: '13px', margin: 0, textAlign: 'center' }}>
                  Location found — zoom and pan to frame the building, then click Next to crop.
                </p>
              )}
            </div>
          );
        }
        // Imported mode
        return (
          <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ color: 'var(--text-primary, #e2e8f0)', marginBottom: '8px' }}>Upload Floor Plan</h2>
            <p style={{ color: 'var(--text-secondary, #94a3b8)', marginBottom: '32px', fontSize: '14px' }}>
              Upload a PDF or image file of your floor plan.
            </p>
            {!uploadedUrl ? (
              <label style={{
                ...cardStyle,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                padding: '48px 24px',
                cursor: 'pointer',
              }}>
                <input type="file" accept=".png,.jpg,.jpeg,.webp,.pdf" onChange={handleFileUpload} style={{ display: 'none' }} />
                {uploading ? (
                  <><Loader2 size={40} className="spin" style={{ color: '#0ea5e9' }} /><span style={{ color: 'var(--text-secondary, #94a3b8)' }}>Uploading...</span></>
                ) : (
                  <>
                    <Upload size={40} style={{ color: '#0ea5e9' }} />
                    <span style={{ color: 'var(--text-primary, #e2e8f0)', fontSize: '16px', fontWeight: 500 }}>Click to upload</span>
                    <span style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '13px' }}>PNG, JPEG, WebP, or PDF — up to 50MB</span>
                  </>
                )}
              </label>
            ) : (
              <div style={{ ...cardStyle, textAlign: 'left' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ color: '#00d4aa', fontWeight: 500 }}>File uploaded</span>
                  <button onClick={() => { setUploadedUrl(null); setUploadedFile(null); }} style={{ background: 'none', border: 'none', color: 'var(--text-secondary, #94a3b8)', cursor: 'pointer' }}>
                    <X size={16} /> Change
                  </button>
                </div>
                <p style={{ color: 'var(--text-primary, #e2e8f0)', margin: '0 0 4px 0', fontSize: '14px' }}>{uploadedFile?.name}</p>
                {uploadedFile?.type?.startsWith('image/') && (
                  <img src={uploadedUrl} alt="Floor plan preview" style={{ width: '100%', borderRadius: '8px', marginTop: '12px', maxHeight: '300px', objectFit: 'contain', background: '#0f172a' }} />
                )}
              </div>
            )}
          </div>
        );

      // ── Step 3: Crop & Adjust ─────────────────────────────
      case 'crop':
        if (sourceType === 'satellite') {
          return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ color: 'var(--text-primary, #e2e8f0)', margin: 0 }}>Crop Area</h3>
                  <p style={{ color: 'var(--text-secondary, #94a3b8)', margin: '4px 0 0 0', fontSize: '13px' }}>
                    Drag and resize the green rectangle to define the property boundary.
                  </p>
                </div>
                <button className="btn btn-secondary" onClick={enableCropRect}>
                  Reset Crop
                </button>
              </div>
              <div style={{ flex: 1, borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border-color, #334155)', minHeight: '400px' }}>
                <div ref={(el) => {
                  if (el && !mapRef.current) {
                    initSatMap(el);
                    setTimeout(enableCropRect, 500);
                  } else if (el && mapRef.current && !rectangleRef.current) {
                    setTimeout(enableCropRect, 200);
                  }
                }} style={{ width: '100%', height: '100%' }} />
              </div>
              {cropBounds && (
                <p style={{ color: '#00d4aa', fontSize: '12px', margin: 0, textAlign: 'center' }}>
                  Crop defined — adjust the rectangle, then click Next to set scale.
                </p>
              )}
            </div>
          );
        }
        // Imported: show the image with optional rotation
        return (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <h3 style={{ color: 'var(--text-primary, #e2e8f0)', margin: 0 }}>Review Floor Plan</h3>
              <p style={{ color: 'var(--text-secondary, #94a3b8)', margin: '4px 0 0 0', fontSize: '13px' }}>
                Verify your floor plan looks correct. You can skip to the next step.
              </p>
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-tertiary, #0f172a)', borderRadius: '8px', border: '1px solid var(--border-color, #334155)', overflow: 'hidden' }}>
              {uploadedUrl && (
                <img src={uploadedUrl} alt="Floor plan" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        );

      // ── Step 4: Set Scale ─────────────────────────────────
      case 'scale':
        const previewUrl = sourceType === 'satellite' ? capturedImage : uploadedUrl;
        return (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ color: 'var(--text-primary, #e2e8f0)', margin: 0 }}>
                  <Ruler size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Set Scale (Optional)
                </h3>
                <p style={{ color: 'var(--text-secondary, #94a3b8)', margin: '4px 0 0 0', fontSize: '13px' }}>
                  Click two points on a known distance, then enter the real-world measurement.
                </p>
              </div>
              {scalePoints.length > 0 && (
                <button className="btn btn-secondary" onClick={() => setScalePoints([])}>
                  Reset Points
                </button>
              )}
            </div>
            <div style={{ flex: 1, position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border-color, #334155)', background: '#0f172a' }}>
              {previewUrl && (
                <img src={previewUrl} alt="Scale reference" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
              )}
              <canvas
                ref={scaleCanvasRef}
                width={800}
                height={600}
                onClick={handleScaleClick}
                style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                  cursor: 'crosshair',
                }}
              />
            </div>
            {scalePoints.length === 2 && (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <label style={labelStyle}>Distance:</label>
                <input
                  type="number"
                  value={scaleDistance}
                  onChange={(e) => setScaleDistance(e.target.value)}
                  placeholder="e.g. 154"
                  style={{ ...inputStyle, width: '120px' }}
                />
                <select
                  value={scaleUnit}
                  onChange={(e) => setScaleUnit(e.target.value)}
                  style={{ ...inputStyle, width: '100px' }}
                >
                  <option value="feet">feet</option>
                  <option value="meters">meters</option>
                </select>
                {scaleDistance && (
                  <span style={{ color: '#00d4aa', fontSize: '13px' }}>
                    {scaleDistance} {scaleUnit} reference set
                  </span>
                )}
              </div>
            )}
          </div>
        );

      // ── Step 5: Save ──────────────────────────────────────
      case 'save':
        return (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 style={{ color: 'var(--text-primary, #e2e8f0)', marginBottom: '24px', textAlign: 'center' }}>
              <Save size={22} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
              Save Site Map
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Map Name *</label>
                <input type="text" value={surveyName} onChange={(e) => setSurveyName(e.target.value)} placeholder="e.g. Main Building - 1st Floor" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              {/* Preview */}
              <div style={{ ...cardStyle, padding: '16px' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                  <div style={{ width: '160px', height: '120px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, background: '#0f172a' }}>
                    <img
                      src={sourceType === 'satellite' ? capturedImage : uploadedUrl}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                  <div style={{ textAlign: 'left', fontSize: '13px', color: 'var(--text-secondary, #94a3b8)' }}>
                    <p style={{ margin: '0 0 4px 0', color: 'var(--text-primary, #e2e8f0)', fontWeight: 500 }}>
                      {surveyName || 'Untitled Map'}
                    </p>
                    <p style={{ margin: '0 0 2px 0' }}>Type: {sourceType === 'satellite' ? 'Satellite' : 'Imported Floor Plan'}</p>
                    {address && <p style={{ margin: '0 0 2px 0' }}>Address: {address}</p>}
                    {scaleDistance && <p style={{ margin: '0 0 2px 0' }}>Scale: {scaleDistance} {scaleUnit}</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ── Main Render ─────────────────────────────────────────
  return (
    <div className="md-page" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title"><MapIcon size={22} /> New Site Map</h1>
          <p className="page-subtitle">Step {step + 1} of {STEPS.length}: {STEPS[step].label}</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/map')}>
            <X size={16} /> Cancel
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        {STEPS.map((s, i) => (
          <div key={s.id} style={{
            flex: 1,
            height: '4px',
            borderRadius: '2px',
            background: i <= step ? '#00d4aa' : 'var(--border-color, #334155)',
            transition: 'background 0.3s',
          }} />
        ))}
      </div>

      {/* Step content */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {renderStepContent()}
      </div>

      {/* Footer nav */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '16px',
        borderTop: '1px solid var(--border-color, #334155)',
        marginTop: '12px',
      }}>
        <button
          className="btn btn-secondary"
          onClick={goBack}
          disabled={step === 0}
          style={{ opacity: step === 0 ? 0.4 : 1 }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div style={{ display: 'flex', gap: '4px' }}>
          {STEPS.map((s, i) => (
            <span key={s.id} style={{
              width: 8, height: 8, borderRadius: '50%',
              background: i === step ? '#00d4aa' : i < step ? 'rgba(0,212,170,0.4)' : 'var(--border-color, #334155)',
            }} />
          ))}
        </div>
        <button
          className="btn btn-primary"
          onClick={goNext}
          disabled={!canAdvance() || saving}
        >
          {saving ? (
            <><Loader2 size={16} className="spin" /> Saving...</>
          ) : step === STEPS.length - 1 ? (
            <><Check size={16} /> Save Map</>
          ) : (
            <>Next <ArrowRight size={16} /></>
          )}
        </button>
      </div>
    </div>
  );
}
